import { useState, useEffect } from 'react';
import api from '../../API.js';
import styles from '../../styles/agents.module.css';

function CollectionSelector({ selectedCollections = [], onChange, disabled = false }) {
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchCollections();
    }, []);

    const fetchCollections = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await api.get('agent/rag/collections');
            
            if (response.status === 503 || 
                (response.data && typeof response.data === 'object' && response.data.code === 'LLM_SERVICES_UNAVAILABLE')) {
                setCollections([]);
                setError('LLM services not available. Collections cannot be loaded.');
                return;
            }
            
            if (response.status === 200 && response.data.collections) {
                setCollections(response.data.collections);
            } else {
                throw new Error(`Failed to fetch collections: ${response.status}`);
            }
        } catch (err) {
            console.error('Error fetching collections:', err);
            setError('Failed to load collections: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const filteredCollections = collections.filter(coll =>
        coll.collectionName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleToggle = (collectionName) => {
        if (disabled) return;
        
        const isSelected = selectedCollections.includes(collectionName);
        if (isSelected) {
            onChange(selectedCollections.filter(c => c !== collectionName));
        } else {
            onChange([...selectedCollections, collectionName]);
        }
    };

    if (loading) {
        return <div className={styles.loading}>Loading collections...</div>;
    }

    if (error && collections.length === 0) {
        return (
            <div className={styles.info}>
                {error}
            </div>
        );
    }

    return (
        <div className={styles.formGroup}>
            <label className={styles.formLabel}>
                Collections
            </label>
            
            {collections.length > 5 && (
                <input
                    type="text"
                    placeholder="Search collections..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles.formInput}
                    style={{ marginBottom: '0.75em' }}
                />
            )}
            
            <div style={{
                border: '1px solid #ddd',
                borderRadius: '6px',
                maxHeight: '200px',
                overflowY: 'auto',
                padding: '0.5em'
            }}>
                {filteredCollections.length === 0 ? (
                    <div style={{ padding: '1em', textAlign: 'center', color: '#586069' }}>
                        {searchTerm ? 'No collections match your search' : 'No collections available'}
                    </div>
                ) : (
                    filteredCollections.map(coll => {
                        const isSelected = selectedCollections.includes(coll.collectionName);
                        return (
                            <label
                                key={coll.collectionName}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '0.5em',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    opacity: disabled ? 0.6 : 1,
                                    borderRadius: '4px',
                                    marginBottom: '0.25em'
                                }}
                                onMouseEnter={(e) => {
                                    if (!disabled) e.currentTarget.style.backgroundColor = '#f6f8fa';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleToggle(coll.collectionName)}
                                    disabled={disabled}
                                    style={{ marginRight: '0.75em' }}
                                />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 500, color: '#24292e' }}>
                                        {coll.collectionName}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#586069' }}>
                                        {coll.documentCount || 0} documents, {coll.chunkCount || 0} chunks
                                    </div>
                                </div>
                            </label>
                        );
                    })
                )}
            </div>
            
            {selectedCollections.length > 0 && (
                <div style={{ marginTop: '0.75em', fontSize: '13px', color: '#586069' }}>
                    Selected: {selectedCollections.join(', ')}
                </div>
            )}
            
            {error && collections.length > 0 && (
                <div className={styles.formHelp} style={{ color: '#ff9800' }}>
                    {error}
                </div>
            )}
        </div>
    );
}

export default CollectionSelector;
